import React from 'react';
import { Link } from 'react-router-dom';
import { Disc, CheckCircle2, Clock, Shield, Sparkles, ArrowRight } from 'lucide-react';
import WorkGallery from '../../components/WorkGallery';

const AlloyWheelRepair = () => {
  const workImages = [
    '/siteimages/image_0017.jpeg',
    '/siteimages/image_0023.jpg',
    '/siteimages/image_0027.jpg',
    '/siteimages/image_0037.jpg',
  ];

  return (
    <div>
      <section className="bg-gradient-to-r from-dark-navy to-dark-teal text-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-3 mb-4">
            <Disc className="w-12 h-12 text-bmw-blue" />
            <h1 className="text-4xl md:text-5xl font-heading font-bold">
              Alloy Wheel Repair
            </h1>
          </div>
          <p className="text-lg md:text-xl text-soft-grey max-w-3xl">
            Professional alloy wheel refurbishment and repair services to restore your wheels to pristine condition.
          </p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-heading font-bold text-dark-navy mb-6">
                Complete <span className="text-bmw-blue">Wheel Refurbishment</span>
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Damaged alloy wheels can significantly detract from your vehicle's appearance. Our comprehensive 
                wheel repair service handles everything from minor kerb damage to complete refurbishment, restoring 
                your wheels to their original factory finish.
              </p>
              <p className="text-gray-600 mb-6 leading-relaxed">
                We use specialist equipment and techniques to repair cracks, straighten bent wheels, and eliminate 
                scuffs and scratches. Our powder coating process ensures a durable, long-lasting finish that 
                matches your wheel's original appearance.
              </p>

              <div className="bg-bmw-blue bg-opacity-10 border-l-4 border-bmw-blue p-6 rounded-r-lg">
                <h3 className="font-bold text-dark-navy mb-2 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-bmw-blue" />
                  Factory-Standard Finish
                </h3>
                <p className="text-gray-600">
                  Our precision color matching and professional finishing techniques deliver results that match or exceed factory standards.
                </p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-off-white p-6 rounded-lg">
                <h3 className="text-xl font-bold text-dark-navy mb-4">What We Repair</h3>
                <ul className="space-y-3">
                  {[
                    'Kerb damage and scuffs',
                    'Minor cracks and chips',
                    'Scratches and scrapes',
                    'Buckled and bent wheels',
                    'Corrosion and oxidation',
                    'Paint peeling and fading',
                    'Complete wheel refurbishment',
                    'Diamond-cut wheel restoration',
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-bmw-blue flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-racing-red bg-opacity-10 border border-racing-red p-6 rounded-lg">
                <h3 className="font-bold text-dark-navy mb-3">Wheel Repair Quote</h3>
                <p className="text-gray-600 mb-4">
                  Get a competitive quote for your alloy wheel repair. Fast turnaround available.
                </p>
                <Link 
                  to="/contact" 
                  className="inline-flex items-center gap-2 bg-racing-red text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transition"
                >
                  Get Free Quote
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-dark-navy text-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-bmw-blue rounded-full mb-4">
                <Clock className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold mb-2">Quick Service</h3>
              <p className="text-soft-grey">
                Most wheel repairs completed within 2-4 days for fast results.
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-bmw-blue rounded-full mb-4">
                <Shield className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold mb-2">Quality Guarantee</h3>
              <p className="text-soft-grey">
                All wheel refurbishment backed by our workmanship guarantee.
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-bmw-blue rounded-full mb-4">
                <Sparkles className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold mb-2">Premium Finish</h3>
              <p className="text-soft-grey">
                Professional powder coating and finishing for lasting durability.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Work Gallery */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <WorkGallery images={workImages} title="Alloy Wheel Refurbishment Gallery" />
        </div>
      </section>

      <section className="py-16 bg-bmw-blue text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
            Ready to Restore Your Wheels?
          </h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">
            Contact us today for a free wheel repair assessment and competitive quote.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="bg-racing-red text-white px-8 py-4 rounded-lg font-semibold hover:bg-red-700 transition shadow-lg inline-flex items-center justify-center gap-2"
            >
              Get Free Quote
              <ArrowRight />
            </Link>
            <a 
              href="tel:08000452446" 
              className="bg-white text-bmw-blue px-8 py-4 rounded-lg font-semibold hover:bg-off-white transition shadow-lg"
            >
              Call: 0800 045 2446
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AlloyWheelRepair;
